/**
 * SmartPickupRecommendation — recommended-pickup chooser.
 *
 * "How would you like to get your pickup recommendation?" — three ways:
 *   1. I know my departure/arrival time → a BUTTON that opens the flight-type
 *      modal (Image #9): International/Domestic toggle + an expandable time
 *      list. Picking a time computes from it and goes to SmartPickupResult
 *      (time mode, no flight header).
 *   2. I know my flight number → "Add" looks the flight up via flightService,
 *      then goes to SmartPickupResult (flight mode, with flight header).
 *   3. Set Pickup Time Manually → back to the wheel, scrolled into view.
 *
 * There is no separate "Get pickup recommendation" button — each option
 * proceeds via its own action (modal time-pick / Add / manual row).
 *
 * Direction-aware: a fromAirport trip looks up the ARRIVAL and hides option 1
 * entirely (only the lookup knows the real landing); a toAirport trip asks for
 * the DEPARTURE and offers both options.
 */
import React, {useState, useMemo, useEffect, useRef} from "react";
import {createPortal} from "react-dom";
import {MODAL_CLOSE_MS} from "@/utils/modalAnim.js";
import DatePicker from "react-datepicker";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {flightService} from "@/services/flightService";
import {tripService} from "@/services/tripService";
import {isNearAirport} from "@/utils/airports";
import {getHopnMinDate, getHopnMaxDate} from "@/utils/filesConstants.js";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import { tripNow, parseRideDate } from "@/utils/dateUtils";
import {img} from "@/utils/pluginImg.js";

// react-datepicker's own CSS is deliberately NOT imported here: WordPress only
// enqueues styles.css, so a JS-imported CSS chunk would never reach the page. The
// calendar instead inherits the .hopn-date-header rules already in styles.scss —
// the same ones the production date step uses.

// Local YYYY-MM-DD (never toISOString(), which would roll back a day west of UTC).
const dateToYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Light client-side check only — the flight API is the real validator. Matches
// "AC878", "AC 878", "LH1234", "5X123".
const isValidFlightNumber = (v) => /^[A-Za-z0-9]{2,3}\s?\d{1,4}[A-Za-z]?$/.test(v.trim());

// "Tuesday, Feb 16" — the label on the date toggle above the calendar.
const longDateLabel = (d) =>
    d ? d.toLocaleDateString("en-US", {weekday: "long", month: "short", day: "numeric"}) : "Select a date";

// 24h "HH:MM" → local Date on the stored ride date.
const buildFlightTime = (rideDate, hhmm) => {
    if (!rideDate || !hhmm) return null;
    const ymd = String(rideDate).slice(0, 10);
    const [y, m, d] = ymd.split("-").map(Number);
    const [hh, mm] = hhmm.split(":").map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return new Date(y, m - 1, d, hh, mm, 0, 0);
};

// "HH:MM" (24h) → "h:mm AM/PM"
const label12h = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

export function SmartPickupRecommendation(props) {

    // Editing an existing booking — the flight card's pencil on orderDetails, or the
    // /edit and /confirm pages (which drive this through onPick). The trip already HAS
    // a date, so asking for the flight date again is a question with one possible
    // answer — and the calendar's Calculate would rewrite it on the way through. The
    // lookup still needs a date; it uses the booking's, which is what `selectedDate`
    // is already seeded from.
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true || !!props.onPick;

    // Narrower flag for the BOOKING MODAL's edit mode only. It gates hiding the typed-time
    // and manual options, which is right when this screen was opened from the flight
    // card's pencil — but wrong on /edit and /confirm, where the chooser is the general
    // pickup-recommendation entry and a non-flight trip would be left with no way to set
    // a time at all. Those pages are `props.onPick`.
    const isModalEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true && !props.onPick;

    // Direction: destinations = [pickup, dropoff, ...]; dropoff is index 1.
    //
    // Every read below prefers a PROP and falls back to session storage. The booking
    // flow passes nothing and keeps its storage-driven behaviour; the edit/confirm
    // pages have no booking in progress, so they hand in the saved trip's details and
    // take the result through onPick/onManual instead of writing booking keys. That
    // keeps the two contexts from sharing mutable state.
    const destinations = props.destinations ?? storage.get(STORAGE_KEYS.DESTINATIONS, []);
    const firstDest = destinations[0];
    const lastDest = destinations[1];
    const firstAirport = firstDest?.lat && firstDest?.lng ? isNearAirport(firstDest.lat, firstDest.lng) : null;
    const lastAirport = lastDest?.lat && lastDest?.lng ? isNearAirport(lastDest.lat, lastDest.lng) : null;
    const isFromAirport = !!firstAirport;
    const isToAirport = !!lastAirport;

    // The airport this leg is tied to (pickup for fromAirport, dropoff for toAirport).
    // The flight side that MUST match it: fromAirport → ARRIVAL, toAirport → DEPARTURE.
    const selectedAirportCode = isFromAirport
        ? (firstAirport && firstAirport.code)
        : (isToAirport ? (lastAirport && lastAirport.code) : null);

    // null = matches; else a reason. Mirrors the production smartTimeSelect logic.
    const flightMismatchReason = (fd) => {
        if (!fd || !selectedAirportCode) return null;
        const sel = String(selectedAirportCode).toUpperCase();
        const dep = (fd?.departure?.airport?.iata || "").toUpperCase();
        const arr = (fd?.arrival?.airport?.iata || "").toUpperCase();
        const wanted = isFromAirport ? arr : dep;   // side that must equal `sel`
        const other = isFromAirport ? dep : arr;
        if (wanted === sel) return null;
        if (other && other === sel) return isFromAirport ? "wrongDirectionDeparture" : "wrongDirectionArrival";
        if (!wanted) return null;
        return "generic";
    };
    // Cancelled: AeroDataBox status starts with "cancel" (Canceled / CanceledUncertain).
    const isFlightCancelled = (fd) =>
        typeof fd?.status === "string" && fd.status.trim().toLowerCase().startsWith("cancel");

    // The provider being unreachable is a block like the others — the passenger is stopped
    // and offered a way through — but it is not about THEIR flight, so it carries its own
    // reason rather than a `mismatch:` one.
    const BLOCK_UNAVAILABLE = "unavailable";

    const MISMATCH_MESSAGES = {
        generic: "Your flight doesn't match your selected airport location.",
        wrongDirectionArrival: "The flight you selected is an arrival flight. For a ride to the airport, please select a departure flight instead.",
        wrongDirectionDeparture: "The flight you selected is a departure flight. For a ride from the airport, please select an arrival flight instead.",
        cancelled: "Your flight is cancelled. Do you still want to continue?",
    };

    // 5-minute time options across the day — same granularity as the date/time wheel,
    // so a flight time can be entered exactly rather than snapped to the nearest quarter.
    const TIME_OPTIONS = useMemo(() => {
        const opts = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 5) {
                const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                opts.push({value, label: label12h(value)});
            }
        }
        return opts;
    }, []);

    const defaultTime = useMemo(() => {
        const toSlot = (d) => {
            const m = Math.round(d.getMinutes() / 5) * 5;
            const mm = m >= 60 ? 55 : m;
            return `${String(d.getHours()).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
        };
        // The flight time the passenger last entered HERE wins. Coming back to this screen
        // has to show what they chose — seeding from RIDE_TIME alone made the field snap
        // back to the pickup time on every return, so an edited flight time looked ignored.
        // (Cleared by the flight-number path, which then falls through to the ride time.)
        const saved = props.enteredTime ?? storage.get(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TIME, null);
        if (saved) {
            const d = new Date(saved);
            if (!isNaN(d.getTime())) return toSlot(d);
        }
        const rideTime = props.rideTime ?? storage.get(STORAGE_KEYS.RIDE_TIME);
        if (rideTime) {
            const d = new Date(rideTime);
            if (!isNaN(d.getTime())) return toSlot(d);
        }
        // Nothing stored at all: fall back the way the WHEEL does — the next 5-minute slot
        // from now — rather than a hardcoded noon, so the two screens can't disagree.
        return toSlot(tripNow());
        // Seeds `selectedTime` via useState, so recomputing later can't move a field the
        // user is editing — the deps are here to keep the memo honest about what it reads.
    }, [props.enteredTime, props.rideTime]);

    // fromAirport renders the flight-number card only, so that is the active one there;
    // toAirport starts on the time card.
    const [activeSection, setActiveSection] = useState(isFromAirport || isModalEditMode ? "flight" : "time");
    const [selectedTime, setSelectedTime] = useState(defaultTime);
    // Same reason as defaultTime: the popup must reopen on the type they picked, not
    // reset to International every time they come back.
    const [flightType, setFlightType] = useState(() => props.flightType ?? storage.get(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TYPE) ?? "International");
    const [pickerOpen, setPickerOpen] = useState(false);
    // Mobile close animation: kept mounted while `is-closing` plays, then unmounted.
    const [pickerClosing, setPickerClosing] = useState(false);
    const closeTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(closeTimerRef.current), []);
    // The time path is a two-screen wizard: pick the flight type + TIME, then the flight
    // DATE. 'time' | 'date'. (The flight-number path has its own single calendar screen,
    // driven by pickerMode below.)
    const [timeStep, setTimeStep] = useState("time");
    // Which variant the flight-type popup shows: "time" = the time list (option 1),
    // "flight" = a calendar for the flight-number lookup date (option 2).
    const [pickerMode, setPickerMode] = useState("time");
    const [selectedDate, setSelectedDate] = useState(() => {
        const rd = props.rideDate ?? storage.get(STORAGE_KEYS.RIDE_DATE);
        const d = parseRideDate(rd);
        return d || getHopnMinDate();
    });
    const [listOpen, setListOpen] = useState(true);
    const [calOpen, setCalOpen] = useState(true);
    const [flightNumber, setFlightNumber] = useState(props.flightNumber ?? storage.get(STORAGE_KEYS.FLIGHT_NUMBER) ?? "");
    // The flight number currently APPLIED to the booking — not the same thing as what is
    // in the box. Arriving with one already set is the "applied" state, and editing the
    // text has to be able to undo it, so the two are tracked separately (same shape as
    // the coupon field).
    const [appliedFlight, setAppliedFlight] = useState(props.flightNumber ?? storage.get(STORAGE_KEYS.FLIGHT_NUMBER) ?? "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    // A looked-up flight that is blocked (airport mismatch / cancelled) waits here
    // until the user clicks "Proceed anyway"; only then is it committed.
    const [pendingFlight, setPendingFlight] = useState(null);
    // YMD the pendingFlight was actually fetched for, so Calculate can tell whether
    // the calendar has since moved to a different day.
    const [fetchedForDate, setFetchedForDate] = useState(null);
    const [blockReason, setBlockReason] = useState(null); // BLOCK_UNAVAILABLE | 'cancelled' | `mismatch:<reason>`
    // Which blockReason the user already waved through with "Proceed anyway". The
    // re-fetch on Calculate hits the same mismatch on the new date, so without this
    // it would bounce them back to the warning they had just accepted.
    const [acceptedBlock, setAcceptedBlock] = useState(null);
    // Done pressed on a from-airport trip with no flight — see handleDone. Shown as the
    // dark bubble over the button (.hopn-submit-tooltip), the same treatment the
    // travelingDateTime step uses for its "Add flight number" prompt.
    const [showDoneTooltip, setShowDoneTooltip] = useState(false);
    // Calculate is doing its route re-quote — keeps the footer button busy so the moment
    // isn't committed twice.
    const [calculating, setCalculating] = useState(false);

    const listRef = useRef(null);
    const selectedRef = useRef(null);

    // Center the current time in the list whenever it (re)opens.
    useEffect(() => {
        if (pickerOpen && pickerMode === "time" && timeStep === "time" && listOpen && selectedRef.current) {
            selectedRef.current.scrollIntoView({block: "center"});
        }
    }, [pickerOpen, pickerMode, timeStep, listOpen]);

    const openPicker = () => {
        setActiveSection("time");
        setPickerMode("time");
        // Always reopen on the time screen — the field on the option card shows the time,
        // so tapping it has to land on the list that changes it.
        setTimeStep("time");
        setCalOpen(true);
        setListOpen(true);
        setPickerOpen(true);
        setError("");
    };

    // Open the SAME popup in calendar mode. Only ever called once a flight has
    // passed validation (or the user overrode it via "Proceed anyway").
    const openFlightDate = () => {
        setPickerMode("flight");
        setCalOpen(true);
        setPickerOpen(true);
    };

    /**
     * The picker is lifted out to <body> so it renders where a real nested modal
     * renders — a sibling of the modal overlay, not a descendant of this step.
     *
     * DESKTOP used to render it inline, as a position:absolute .upper-modal measured
     * against .hopn-modal-container. That tied the calendar's height to the parent
     * step's: this step is short, so the popup's 90% cap left the month grid scrolling
     * inside a box that was itself taller than the modal it appeared to belong to, and
     * the two boxes read as one broken screen. Portalled, it is a standalone
     * full-viewport modal (see .hopn-ft-popup.upper-modal in styles.scss) whose size
     * owes nothing to whatever is behind it.
     *
     * On mobile the same lift keeps mount/unmount from reflowing the parent step, whose
     * height visibly jumped as the sheet opened.
     */
    const renderPicker = (node) => createPortal(node, document.body);

    /**
     * Dismiss the picker. On MOBILE it presents as a nested modal, and a nested modal
     * has to play its slide-down before it leaves — real react-modal steps get that
     * from `closeTimeoutMS` + the `--before-close` classes. This picker is a plain
     * conditional div, so the exit is staged by hand: flag `is-closing` (CSS plays
     * fadeOut + slideDown), then unmount after MODAL_CLOSE_MS. Desktop is a centred
     * popup, not a sheet, and dismisses instantly as before.
     */
    const closePicker = () => {
        if (window.innerWidth >= 576) {
            setPickerOpen(false);
            return;
        }
        if (pickerClosing) return;          // already animating out
        setPickerClosing(true);
        closeTimerRef.current = setTimeout(() => {
            setPickerClosing(false);
            setPickerOpen(false);
        }, MODAL_CLOSE_MS);
    };

    // The code in the box is the flight actually on the booking, so "Add" has nothing
    // left to do — the button becomes the ✕ that takes it off again.
    const isFlightApplied = !!appliedFlight
        && flightNumber.trim().toUpperCase() === appliedFlight.trim().toUpperCase();

    // Take the flight back OFF the booking. Nothing to reprice — a flight doesn't change
    // the fare — but the stored lookup drives the recommended pickup downstream, so it
    // has to go with the number rather than linger as an orphan.
    const unapplyFlight = () => {
        if (!appliedFlight) return;
        setAppliedFlight("");
        storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
        storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
        // Any half-finished lookup belonged to the number being removed.
        setPendingFlight(null);
        setFetchedForDate(null);
        setBlockReason(null);
        setAcceptedBlock(null);
        setError("");
    };

    // Typing in the box: a number that no longer matches the applied one can't keep a
    // flight attached to the booking.
    const changeFlightNumber = (next) => {
        setActiveSection("flight");
        setFlightNumber(next);
        setError("");
        setShowDoneTooltip(false);   // they're acting on the prompt
        setPendingFlight(null);
        setBlockReason(null);
        setAcceptedBlock(null);
        if (appliedFlight && next.trim().toUpperCase() !== appliedFlight.trim().toUpperCase()) {
            unapplyFlight();
        }
    };

    // The ✕: empty the box AND drop the flight.
    const clearFlight = () => {
        setFlightNumber("");
        unapplyFlight();
    };

    // Done (booking-modal edit mode only) — leave without changing anything. On a
    // FROM-airport trip the flight is the whole basis of the pickup time, and the ✕ above
    // can now take it away, so leaving with none would send the passenger back to
    // orderDetails with a booking that has nothing to time itself against. toAirport is
    // free to leave: a typed departure time works there without a flight.
    const handleDone = () => {
        if (isFromAirport && !storage.get(STORAGE_KEYS.FLIGHT_NUMBER)) {
            setShowDoneTooltip(true);
            return;
        }
        props.setStep("orderDetails");
    };

    const goManual = () => {
        if (props.onManual) { props.onManual(); return; }
        storage.set(STORAGE_KEYS.SMART_PICKUP_SCROLL_PICKER, "1");
        props.setStep("travelingDateTime");
    };

    /**
     * Re-quote the route before showing the result, so the drive time the recommendation
     * is built from is the one predicted for the ride's own pickup window.
     *
     * ── This used to commit RIDE_DATE / RIDE_TIME, and must not ──────────────────────
     *
     * Every date and time on THIS screen belongs to the FLIGHT — when it departs, when
     * it lands, which day it flies. RIDE_DATE / RIDE_TIME are the RIDE's, and the two are
     * rarely the same moment: a 1 AM departure with a three-hour cushion is a pickup the
     * evening before. Writing the flight's moment into the ride here meant that entering
     * flight details silently moved the booking — and left it there if the passenger
     * backed out of the recommendation instead of accepting it.
     *
     * The ONLY thing that sets the ride's pickup is "Use Recommended Pickup" on
     * smartPickupResult, which writes the recommended moment. This screen reads the
     * stored ride date to seed its calendar and to look flights up against; it no longer
     * writes to it.
     *
     * The refresh below stays: it is keyed on the stored pickup window and no-ops when
     * that window has already been quoted, so with nothing being written it is usually
     * free — and still correct on the run where the wheel's time has not been quoted yet.
     */
    const refreshRouteBeforeResult = async () => {
        setCalculating(true);
        try {
            await tripService.refreshRouteForPickupTime();
        } finally {
            setCalculating(false);
        }
    };

    // Calculate button: compute from the selected flight type + time → result.
    const calculate = async () => {
        // The date comes from THIS popup's calendar (step 1), not the stored ride date —
        // the flight can be on a different day than the pickup the wheel was left on.
        const dt = buildFlightTime(dateToYMD(selectedDate), selectedTime);
        if (!dt) {
            setError("Please choose a time.");
            setPickerOpen(false);
            return;
        }
        // `date` rides along for the caller's information only — SmartPickupFlow does not
        // apply it. The trip's schedule moves on "Use Recommended Pickup" and nowhere
        // else; this screen's dates belong to the FLIGHT.
        if (props.onPick) {
            props.onPick({flightDetails: null, enteredTime: dt.toString(), flightType, date: dateToYMD(selectedDate)});
            return;
        }
        storage.set(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TIME, dt.toString());
        storage.set(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TYPE, flightType);
        storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
        storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
        // The flight's moment, NOT the ride's — the wheel is left alone until "Use
        // Recommended Pickup" commits the recommendation itself.
        await refreshRouteBeforeResult();
        props.setStep("smartPickupResult");
    };

    // Commit a looked-up flight and go to the result.
    const commitFlight = async (fd) => {
        setAppliedFlight(flightNumber.trim());
        if (props.onPick) {
            props.onPick({
                flightDetails: fd,
                enteredTime: null,
                flightNumber: flightNumber.trim(),
                flightType: flightService.getFlightType(fd) || flightType,
                date: dateToYMD(selectedDate),
            });
            return;
        }
        storage.set(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, fd);
        storage.set(STORAGE_KEYS.FLIGHT_NUMBER, flightNumber.trim());
        // The result step reads this for the airport buffer (International 3h /
        // Domestic 2h). Flight mode has no toggle, so classify the flight from its own
        // airports and only fall back to the toggle when the API omits a country.
        storage.set(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TYPE, flightService.getFlightType(fd) || flightType);
        storage.remove(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TIME);
        // The flight is now on the booking; the ride's own date and time are not touched
        // by it. Only "Use Recommended Pickup" moves those.
        await refreshRouteBeforeResult();
        props.setStep("smartPickupResult");
    };

    // One lookup used by both "Add" (initial validation) and "Calculate" (re-check
    // when the calendar moved to a different day than we validated against).
    const lookupFlight = async (dateObj) => {
        setLoading(true);
        const role = isFromAirport ? "Arrival" : "Departure";
        const result = await flightService.getFlightDetails(flightNumber.trim(), dateToYMD(dateObj), role);
        setLoading(false);

        if (!result.success) {
            // A provider outage is a different failure from "no such flight": nothing is
            // wrong with what the passenger typed, so it goes to the warning card with a
            // Proceed anyway instead of dead error text they can only stare at.
            if (result.unavailable) return {unavailable: true};
            return {error: result.error || "Failed to fetch flight details."};
        }
        if (!result.data || result.data.length === 0) {
            return {error: "No flight found for that number and date."};
        }

        const fd = result.data[0];
        const mismatch = flightMismatchReason(fd);
        const cancelled = isFlightCancelled(fd);
        return {
            fd,
            block: (mismatch || cancelled)
                ? (cancelled && !mismatch ? "cancelled" : `mismatch:${mismatch}`)
                : null,
        };
    };

    // "Add": validation happens HERE, before any calendar. A bad number, a flight
    // that can't be found, or an airport mismatch / cancellation is surfaced on the
    // page; the calendar only opens once the flight is accepted.
    const validateFlight = async () => {
        setActiveSection("flight");
        setError("");
        setPendingFlight(null);
        setBlockReason(null);
        setAcceptedBlock(null);   // a fresh Add re-asks for any override

        const num = flightNumber.trim();
        if (!num) {
            setError("Please enter your flight number.");
            return;
        }
        if (!isValidFlightNumber(num)) {
            setError("Please enter a valid flight number, e.g. AC878.");
            return;
        }

        const {fd, error: lookupError, block, unavailable} = await lookupFlight(selectedDate);
        if (unavailable) {
            setBlockReason(BLOCK_UNAVAILABLE);
            return;
        }
        if (lookupError) {
            setError(lookupError);
            return;
        }

        setPendingFlight(fd);
        setFetchedForDate(dateToYMD(selectedDate));
        if (block) {
            // Hold the flight and show the warning — the calendar waits until the
            // user accepts it with "Proceed anyway".
            setBlockReason(block);
            return;
        }

        // Edit mode skips the date screen entirely (see isEditMode).
        if (isEditMode) {
            await commitFlight(fd);
            return;
        }

        openFlightDate();
    };

    // Warning accepted → remember it, then pick the date. The warning stays on the
    // page so the user keeps the context if they close the calendar again.
    const proceedAnyway = async () => {
        // Provider outage: there is no flight to carry forward, so accepting the warning
        // keeps the typed number on the booking (the API requires one on a from-airport
        // trip) and hands the passenger to manual time entry — no details, no
        // recommendation. Same resolution the older smartTimeSelect card offers.
        //
        // The storage write is skipped on /edit and /confirm for the same reason
        // commitFlight skips it: those pages hand their result back through props and
        // must not touch the booking flow's keys. goManual() already routes both ways.
        if (blockReason === BLOCK_UNAVAILABLE) {
            if (!props.onManual) storage.set(STORAGE_KEYS.FLIGHT_NUMBER, flightNumber.trim());
            goManual();
            return;
        }
        if (!pendingFlight) return;
        setAcceptedBlock(blockReason);
        // Same in edit mode: accepted warning → commit against the booking's own date.
        if (isEditMode) {
            await commitFlight(pendingFlight);
            return;
        }
        openFlightDate();
    };

    // Calculate (flight mode). The flight was validated against whatever date was
    // selected at "Add" time; if the calendar has since moved, re-fetch so we commit
    // the flight that actually operates on the chosen day. Without this, picking a
    // new date silently kept the previously fetched flight.
    const confirmFlightDate = async () => {
        if (!pendingFlight) return;
        const wanted = dateToYMD(selectedDate);

        if (wanted === fetchedForDate) {
            // The popup deliberately stays OPEN while this runs. Closing it first made
            // Calculate look finished — the calendar vanished, then a beat later the step
            // changed. The footer button spins instead (see `calculating`), and the step
            // change unmounts the popup, so the transition reads as one action. Same as
            // the typed-time path, which never closed early.
            await commitFlight(pendingFlight);
            return;
        }

        setError("");
        const {fd, error: lookupError, block, unavailable} = await lookupFlight(selectedDate);
        if (unavailable) {
            // Not something a different date can fix, so the calendar closes and the
            // passenger lands back on the page where Proceed anyway lives.
            setPickerOpen(false);
            setBlockReason(BLOCK_UNAVAILABLE);
            return;
        }
        if (lookupError) {
            setError(lookupError);   // popup stays open so the date can be changed
            return;
        }

        setPendingFlight(fd);
        setFetchedForDate(wanted);
        // Only stop for a problem the user hasn't already accepted. The same flight
        // number on a different day carries the same mismatch, so re-warning here
        // would trap them in Proceed anyway → Calculate → warning → repeat.
        if (block && block !== acceptedBlock) {
            setPickerOpen(false);
            setBlockReason(block);
            return;
        }

        await commitFlight(fd);
    };

    const warningMessage = !blockReason
        ? ""
        : blockReason === BLOCK_UNAVAILABLE
            ? "Failed to get the details from server."
            : blockReason === "cancelled"
                ? MISMATCH_MESSAGES.cancelled
                : (MISMATCH_MESSAGES[blockReason.slice("mismatch:".length)] || MISMATCH_MESSAGES.generic);

    // Which screen the popup is on. The flight-number path is calendar-only; the time
    // path shows the flight type + time list first (step 1), then the calendar (step 2).
    const showCalendar = pickerMode === "flight" || timeStep === "date";
    // Step 1 of the time path only advances the wizard, so it reads "Continue"; every
    // screen that actually commits reads "Calculate".
    const footerLabel = (pickerMode === "time" && timeStep === "time") ? "Continue" : "Calculate";

    const timeLabels = isFromAirport
        ? {title: "I know my arrival time", sub: "Enter your flight arrival time"}
        : {title: "I know my departure time", sub: "Enter your flight departure time"};

    // Flight-type popup helper line — direction-aware.
    const ftSubtitle = isFromAirport
        ? "This helps us estimate your time through baggage & customs."
        : "This helps us calculate the right airport arrival time.";


    return (
        <>
            <ModalHeader
                title="Pickup recommendation"
                extraClasses=""
                totalSteps={0}
                cancelButton={!!props.showClose}
                cancelButtonType="icon"
                ignoreEditMode={true}
                backStep={"travelingDateTime"}
                showBackButton={true}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            <div className={`modalPages-spacing hopn-pickup-recommendation padding-bottom-30${isFromAirport ? " is-from-airport" : ""}`}>
                {/* fromAirport offers only the flight-number path, so a "how would you
                    like…" question has nothing to ask — the head is dropped there. Same in
                    booking-modal edit mode, which also leaves the flight card alone. */}
                {!isFromAirport && !isModalEditMode && (
                    <div className="hopn-pr-head">
                        <h2>How would you like to get your pickup recommendation?</h2>
                        <p>We'll find the ideal pickup time based on your flight time and airline and real-time traffic.</p>
                    </div>
                )}

                {/* The options share one elevated parent card (box-shadow group). */}
                <div className="hopn-pr-group">
                {/* 1 — flight time (button → flight-type modal). Not rendered in two cases,
                    each leaving option 2 as the single path:
                    • fromAirport — a typed time is enough to work back from a departure,
                      but a from-airport ride hangs on the actual landing (delays, revised
                      times, terminal), which only the flight lookup knows.
                    • booking-modal edit mode — this screen is reached from the FLIGHT
                      card's pencil on orderDetails, so the flight number is the only thing
                      being changed; the pickup time has its own edit pencil. */}
                {!isFromAirport && !isModalEditMode && (
                    <>
                        <div
                            className={`hopn-pr-option ${activeSection === "time" ? "selected" : ""}`}
                            onClick={openPicker}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="hopn-pr-option-head">
                                <h5>{timeLabels.title}</h5>
                                <p>{timeLabels.sub}</p>
                            </div>
                            <button type="button" className="hopn-pr-time-field" onClick={(e) => { e.stopPropagation(); openPicker(); }}>
                                <span className="hopn-pr-field-icon">
                                    <img src={img("clock-bold.svg")} alt=""/>
                                </span>
                                <span className="hopn-pr-time-value">{label12h(selectedTime)}</span>
                                <span className="hopn-pr-field-caret">
                                    <img src={img("arrow-down.svg")} alt=""/>
                                </span>
                            </button>
                        </div>

                        <div className="hopn-pr-or"><span>OR</span></div>
                    </>
                )}

                {/* 2 — flight number */}
                <div
                    className={`hopn-pr-option ${activeSection === "flight" ? "selected" : ""}`}
                    onClick={() => setActiveSection("flight")}
                >
                    <div className="hopn-pr-option-head">
                        <h5>I know my flight number</h5>
                        <p>We'll automatically find your flight details</p>
                    </div>
                    <div className="hopn-pr-flight-field" onClick={(e) => e.stopPropagation()}>
                        <span className="hopn-pr-field-icon">
                            <img src={img("plane-take-off.svg")} alt=""/>
                        </span>
                        <input
                            type="text"
                            name="flight_number"
                            autoComplete="off"
                            value={flightNumber}
                            placeholder="e.g. AC878"
                            onChange={(e) => changeFlightNumber(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    validateFlight();
                                }
                            }}
                        />
                        {/* Once the flight is on the booking there is nothing left to
                            "Add", so the same button becomes the ✕ that removes it —
                            mirroring the coupon field. Editing the number unapplies it
                            and turns the button back into Add. */}
                        {isFlightApplied ? (
                            <button
                                type="button"
                                className="hopn-pr-flight-add is-clear"
                                aria-label="Remove flight"
                                onClick={clearFlight}
                            >
                                <img src={img("bigClose.svg")} alt=""/>
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="hopn-pr-flight-add"
                                disabled={loading}
                                onClick={validateFlight}
                            >
                                {loading ? <Loader color="#1f9d6a"/> : "Add"}
                            </button>
                        )}
                    </div>
                </div>
                </div>{/* .hopn-pr-group */}

                {error && <div className="hopn-pr-error error">{error}</div>}

                {/* Airport mismatch / cancelled flight / provider outage (Proceed anyway). */}
                {blockReason && (
                    <div className="hopn-pr-warning">
                        <span>{warningMessage}</span>
                        <button
                            type="button"
                            className="hopn-pr-warning-proceed"
                            disabled={loading}
                            onClick={proceedAnyway}
                        >
                            Proceed anyway
                        </button>
                    </div>
                )}

                {/* fromAirport drops option 1, which takes the OR divider inside the group
                    with it — leaving the flight card and the manual row reading as two
                    unrelated blocks. This puts the choice back between the two options
                    that direction actually has. (Edit mode has no manual row to divide.) */}
                {isFromAirport && !isModalEditMode && (
                    <div className="hopn-pr-or"><span>OR</span></div>
                )}

                {/* 3 — manual. Hidden in booking-modal edit mode for the same reason as
                    option 1: this screen is only editing the flight, and sending the
                    passenger to the wheel from here would be a second route to a field
                    that already has its own pencil on orderDetails. */}
                {!isModalEditMode && (
                <div className="hopn-pr-manual" onClick={goManual} role="button" tabIndex={0}>
                    <span className="hopn-pr-manual-icon">
                        <img src={img("clock-bold.svg")} alt=""/>
                    </span>
                    <div className="hopn-pr-manual-text">
                        <h5>Set Pickup Time Manually</h5>
                        <p>Choose your own pickup time.</p>
                    </div>
                </div>
                )}
            </div>

            {/* Edit mode has no other way off this screen: the back arrow is suppressed
                (see ModalHeader), the manual row is gone, and "Add" only moves on when a
                flight actually resolves. Done returns to orderDetails, so a passenger who
                opened the flight pencil and changed their mind isn't stuck. */}
            {isModalEditMode && (
                <ModalFooter>
                    <div className="hopn-submit-btn-wrapper">
                        <button
                            type="button"
                            className="hopn-add-passenger-button no-icon"
                            onClick={handleDone}
                        >
                            Done
                        </button>
                        {showDoneTooltip && (
                            <div className="hopn-submit-tooltip visible hopn-pr-done-tooltip">
                                <div className="tooltip-close-row">
                                    <button
                                        type="button"
                                        className="tooltip-close-btn"
                                        onClick={() => setShowDoneTooltip(false)}
                                        aria-label="Close"
                                    >
                                        <img src={img("bigClose.svg")} alt="Close"/>
                                    </button>
                                </div>
                                <p>Flight id is required</p>
                            </div>
                        )}
                    </div>
                </ModalFooter>
            )}

            {/* Flight-type popup (Image #9) — uses the shared .upper-modal popup layout.
                In flight mode the title follows the direction: fromAirport looks the flight
                up by its ARRIVAL date (see the dateRole in lookupFlight), toAirport by its
                departure date. */}
            {(pickerOpen || pickerClosing) && renderPicker(
                <div
                    className={`upper-modal hopn-ft-popup${pickerClosing ? " is-closing" : ""}`}
                    style={{display: "flex"}}
                    onClick={closePicker}
                >
                    <div className="upper-modal-inner" onClick={(e) => e.stopPropagation()}>
                        <ModalHeader
                            inline={true}
                            title={showCalendar
                                ? (isFromAirport ? "Flight arrival date" : "Flight departure date")
                                : "Flight type"}
                            extraClasses=""
                            totalSteps={0}
                            cancelButton={true}
                            cancelButtonType="icon"
                            backStep={null}
                            showBackButton={pickerMode === "time" && timeStep === "date"}
                            enableEditModeOnBackButton={false}
                            onBack={() => setTimeStep("time")}
                            setStep={props.setStep}
                            closeModal={closePicker}
                        />

                        <div className="upper-modal-content padding-bottom-30">
                            <div className="hopn-ft-body modalPages-spacing">
                                {showCalendar ? (
                                    /* Date screen — shared by both paths: the whole flight-number
                                       mode, and step 1 of the time path. No title, helper line or
                                       International/Domestic toggle — just the calendar, full width.
                                       Wrapped in the production calendar classes to pick up the
                                       .hopn-date-header rules in styles.scss. */
                                    <>
                                        <button
                                            type="button"
                                            className={`hopn-ft-time-toggle ${calOpen ? "is-open" : ""}`}
                                            onClick={() => setCalOpen((v) => !v)}
                                        >
                                            <span className="hopn-ft-time-icon">
                                                <img src={img("calender-lite.svg")} alt=""/>
                                            </span>
                                            <span className="hopn-ft-time-value">{longDateLabel(selectedDate)}</span>
                                            <span className="hopn-ft-time-caret">
                                                <img src={img("arrow-down.svg")} alt=""/>
                                            </span>
                                        </button>

                                        {calOpen && (
                                            <div className="hopn-ft-calendar">
                                                <DatePicker
                                                    selected={selectedDate}
                                                    onChange={(d) => { setSelectedDate(d); setError(""); }}
                                                    inline
                                                    minDate={getHopnMinDate()}
                                                    maxDate={getHopnMaxDate()}
                                                    // Two-letter day names starting Sunday (default en-US
                                                    // locale) to match the design — not enGB's Monday start.
                                                    formatWeekDay={(name) => name.slice(0, 2)}
                                                    showPopperArrow={false}
                                                    renderCustomHeader={({
                                                        date,
                                                        decreaseMonth,
                                                        increaseMonth,
                                                        prevMonthButtonDisabled,
                                                        nextMonthButtonDisabled,
                                                    }) => (
                                                        <div className="hopn-ft-cal-head">
                                                            <span className="hopn-ft-cal-month">
                                                                {date.toLocaleDateString("en-US", {month: "short", year: "numeric"})}
                                                            </span>
                                                            <span className="hopn-ft-cal-nav">
                                                                <button
                                                                    type="button"
                                                                    onClick={decreaseMonth}
                                                                    disabled={prevMonthButtonDisabled}
                                                                    aria-label="Previous month"
                                                                >
                                                                    <img src={img("arrow-down.svg")} alt=""/>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={increaseMonth}
                                                                    disabled={nextMonthButtonDisabled}
                                                                    aria-label="Next month"
                                                                >
                                                                    <img src={img("arrow-down.svg")} alt=""/>
                                                                </button>
                                                            </span>
                                                        </div>
                                                    )}
                                                />
                                            </div>
                                        )}
                                        {error && <div className="hopn-pr-error error">{error}</div>}
                                    </>
                                ) : (
                                    <>
                                        <p className="hopn-ft-sub">{ftSubtitle}</p>

                                        <div className="hopn-ft-toggle">
                                            <button
                                                type="button"
                                                className={flightType === "International" ? "active" : ""}
                                                onClick={() => setFlightType("International")}
                                            >
                                                International
                                            </button>
                                            <button
                                                type="button"
                                                className={flightType === "Domestic" ? "active" : ""}
                                                onClick={() => setFlightType("Domestic")}
                                            >
                                                Domestic
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            className={`hopn-ft-time-toggle ${listOpen ? "is-open" : ""}`}
                                            onClick={() => setListOpen((v) => !v)}
                                        >
                                            <span className="hopn-ft-time-icon">
                                                <img src={img("clock-bold.svg")} alt=""/>
                                            </span>
                                            <span className="hopn-ft-time-value">{label12h(selectedTime)}</span>
                                            <span className="hopn-ft-time-caret">
                                                <img src={img("arrow-down.svg")} alt=""/>
                                            </span>
                                        </button>

                                        {listOpen && (
                                            <ul className="hopn-ft-list" ref={listRef}>
                                                {TIME_OPTIONS.map((o) => {
                                                    const isSel = o.value === selectedTime;
                                                    return (
                                                        <li
                                                            key={o.value}
                                                            ref={isSel ? selectedRef : null}
                                                            className={isSel ? "selected" : ""}
                                                            onClick={() => setSelectedTime(o.value)}
                                                        >
                                                            {o.label}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="upper-modal-footer modalPages-spacing padding-bottom-30">
                            <button
                                type="button"
                                className="hopn-add-passenger-button no-icon"
                                disabled={loading || calculating}
                                onClick={pickerMode === "flight"
                                    ? confirmFlightDate
                                    : (timeStep === "time" ? () => setTimeStep("date") : calculate)}
                            >
                                {loading || calculating ? <Loader color="#fff"/> : footerLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default SmartPickupRecommendation;
